/** CLI program name */
export const CLI_NAME = "llmist";

/** CLI program description shown in --help */
export const CLI_DESCRIPTION = "Command line utilities for llmist agents and direct LLM access.";

/** Available CLI commands */
export const COMMANDS = {
  complete: "complete",
  agent: "agent",
  models: "models",
  gadget: "gadget",
  image: "image",
  speech: "speech",
  vision: "vision",
  research: "deep-research",
  init: "init",
  config: "config",
} as const;

/** Valid log level names */
export const LOG_LEVELS = ["silly", "trace", "debug", "info", "warn", "error", "fatal"] as const;
export type CLILogLevel = (typeof LOG_LEVELS)[number];

/** Default model used when --model is not specified */
export const DEFAULT_MODEL = "openai:gpt-5-nano";

/** Command-line option flags */
export const OPTION_FLAGS = {
  model: "-m, --model <identifier>",
  systemPrompt: "-s, --system <prompt>",
  systemPromptFile: "--system-file <path>",
  temperature: "-t, --temperature <value>",
  maxTokens: "--max-tokens <count>",
  maxIterations: "-i, --max-iterations <count>",
  budget: "-b, --budget <amount>",
  gadgetModule: "-g, --gadget <module>",
  logLevel: "--log-level <level>",
  logLlmRequests: "--log-llm-requests",
  noBuiltins: "--no-builtins",
  noBuiltinInteraction: "--no-builtin-interaction",
  quiet: "-q, --quiet",
  // Multimodal input options
  inputImage: "--image <path>",
  inputAudio: "--audio <path>",
  // Image generation options
  imageSize: "--size <size>",
  imageQuality: "--quality <quality>",
  imageCount: "-n, --count <number>",
  imageOutput: "-o, --output <path>",
  // Research options
  researchBackground: "--background",
  researchResume: "--resume <ref>",
  researchCancel: "--cancel <ref>",
  researchJson: "--json",
  researchTimeout: "--timeout <seconds>",
  researchMaxToolCalls: "--max-tool-calls <count>",
  researchOutput: "-o, --output <path>",
  // Speech generation options
  voice: "--voice <name>",
  speechFormat: "--format <format>",
  speechSpeed: "--speed <value>",
  speechOutput: "-o, --output <path>",
  // Rate limiting options
  rateLimitRpm: "--rate-limit-rpm <count>",
  rateLimitTpm: "--rate-limit-tpm <count>",
  rateLimitDaily: "--rate-limit-daily <count>",
  rateLimitSafetyMargin: "--rate-limit-safety-margin <value>",
  noRateLimit: "--no-rate-limit",
  // Retry options
  maxRetries: "--max-retries <count>",
  retryMinTimeout: "--retry-min-timeout <ms>",
  retryMaxTimeout: "--retry-max-timeout <ms>",
  noRetry: "--no-retry",
  // Reasoning options
  reasoning: "--reasoning [effort]",
  noReasoning: "--no-reasoning",
  reasoningBudget: "--reasoning-budget <tokens>",
  // MCP options (plan 1: ad-hoc single-server flag; plan 2 adds full TOML schema)
  mcpServer: "--mcp-server <spec...>",
  mcpTrust: "--mcp-trust <name...>",
} as const;

/** Human-readable descriptions for command-line options */
export const OPTION_DESCRIPTIONS = {
  model: "Model identifier, e.g. openai:gpt-5-nano or anthropic:claude-sonnet-4-5.",
  systemPrompt: "Optional system prompt prepended to the conversation.",
  systemPromptFile: "Path to file containing system prompt (alternative to --system).",
  temperature: "Sampling temperature between 0 and 2.",
  maxTokens: "Maximum number of output tokens requested from the model.",
  maxIterations: "Maximum number of agent loop iterations before exiting.",
  budget: "Maximum spend in USD before the agent loop exits.",
  gadgetModule:
    "Path or module specifier for a gadget export. Repeat to register multiple gadgets.",
  logLevel: "Log level: silly, trace, debug, info, warn, error, fatal.",
  logLlmRequests: "Save LLM requests/responses to the session log directory.",
  noBuiltins: "Disable built-in gadgets (AskUser, TellUser).",
  noBuiltinInteraction: "Disable interactive gadgets (AskUser) while keeping TellUser.",
  quiet: "Suppress all output except content (text and TellUser messages).",
  // Multimodal input descriptions
  inputImage: "Image file to include with the prompt (vision models).",
  inputAudio: "Audio file to include with the prompt (Gemini only).",
  // Image generation descriptions
  imageSize: "Image size/aspect ratio, e.g. '1024x1024', '1:1', '16:9'.",
  imageQuality: "Image quality: 'standard', 'hd', 'low', 'medium', 'high'.",
  imageCount: "Number of images to generate (model dependent, usually 1-4).",
  imageOutput: "Output path for the generated image. Defaults to stdout if not specified.",
  // Deep research descriptions
  researchBackground:
    "Start the job, print its serialized ref (JSON) to stdout, and exit. Resume later with --resume.",
  researchResume: "Re-attach to a running background job from a serialized ref (JSON).",
  researchCancel: "Cancel a background job server-side from a serialized ref (JSON).",
  researchJson:
    "Emit newline-delimited JSON research events to stdout instead of formatted output.",
  researchTimeout: "Client-side time budget in seconds (background jobs keep running server-side).",
  researchMaxToolCalls:
    "Cap on server-side tool calls (cost control; supported by OpenAI research models).",
  researchOutput: "Write the report to a file instead of stdout.",
  // Speech generation descriptions
  voice: "Voice name for speech generation, e.g. 'nova', 'alloy', 'Zephyr'.",
  speechFormat: "Audio format: 'mp3', 'opus', 'aac', 'flac', 'wav', 'pcm'.",
  speechSpeed: "Speech speed multiplier (0.25 to 4.0, default 1.0).",
  speechOutput: "Output path for audio file. Defaults to stdout if not specified.",
  // Rate limiting descriptions
  rateLimitRpm: "Maximum requests per minute (RPM). Overrides config and defaults.",
  rateLimitTpm: "Maximum tokens per minute (TPM). Overrides config and defaults.",
  rateLimitDaily: "Maximum tokens per day. Useful for Gemini free tier.",
  rateLimitSafetyMargin: "Safety margin (0-1). Start throttling at this percentage of limit.",
  noRateLimit: "Disable rate limiting entirely.",
  // Retry descriptions
  maxRetries: "Maximum retry attempts for failed API calls.",
  retryMinTimeout: "Initial retry delay in milliseconds.",
  retryMaxTimeout: "Maximum retry delay in milliseconds.",
  noRetry: "Disable retry logic for API calls.",
  // Reasoning descriptions
  reasoning:
    "Enable reasoning mode. Optional effort: none, low, medium, high, maximum (default: medium).",
  noReasoning: "Disable auto-enabled reasoning for reasoning-capable models.",
  reasoningBudget:
    "Explicit reasoning token budget (Anthropic/Gemini 2.5). Overrides effort level.",
  // MCP descriptions
  mcpServer:
    "Attach an MCP stdio server. Format: '<name>=<command>' optionally followed by args after '--'. Repeat for multiple. Args belong to the most recent --mcp-server until the next --mcp-server.",
  mcpTrust:
    "Skip the stdio command allowlist for the named MCP server. Repeat for multiple servers. See https://llmist.dev/library/advanced/mcp-security/ (CVE-2026-30623).",
} as const;

/** Prefix for summary output written to stderr */
export const SUMMARY_PREFIX = "[llmist]";
